import { Box, Tooltip } from "@mui/material"
import { PULSE } from "../motion"
import { socketsConfigured } from "../config"

/**
 * Whether live delivery is working, said quietly.
 *
 * This used to be an orange outlined chip reading "Live updates off", sitting a
 * few pixels from the page title. Alarm orange next to a heading reads as
 * "something is broken" — but a socket that is reconnecting is a condition, not
 * an incident, and nothing the reader can act on. It is also the app's normal
 * state on a deployment with no sockets engine, so the loudest element on the
 * page was permanent.
 *
 * A dot and a word instead. The colour is on the dot, where it is a status
 * light; the recovery — what actually gets your messages back — is in the
 * tooltip, where somebody who wants it can find it.
 *
 * Three states, not two. A deployment with no sockets engine is not
 * "reconnecting": telling somebody to wait for a connection that is never
 * coming is worse than telling them to reload.
 */
export default function ConnectionStatus({ connected }) {
  const state = connected ? "live" : socketsConfigured ? "reconnecting" : "off"

  const label = { live: "Live", reconnecting: "Reconnecting", off: "Not live" }[state]
  const dot = { live: "success.dark", reconnecting: "warning.main", off: "text.disabled" }[state]
  const title = {
    live: "Live — new messages arrive as they are sent.",
    reconnecting: "Reconnecting. Messages will arrive when the connection is back; reload to fetch them now.",
    off: "Live updates are not available on this deployment. Reload to see new messages.",
  }[state]

  return (
    <Tooltip title={title}>
      <Box
        component="span"
        aria-label={title}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.75,
          height: 28,
          px: 1.25,
          borderRadius: 999,
          bgcolor: "action.hover",
          color: "text.secondary",
          fontSize: 12,
          fontWeight: 500,
          whiteSpace: "nowrap",
        }}
      >
        <Box
          component="span"
          sx={{
            width: 7,
            height: 7,
            borderRadius: "50%",
            bgcolor: dot,
            flexShrink: 0,
            // Only while it is trying. A steady green light is a fact; a
            // blinking amber one is an activity, which is what reconnecting is.
            ...(state === "reconnecting" ? PULSE : null),
          }}
        />
        {label}
      </Box>
    </Tooltip>
  )
}
